import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";

import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { BookACallFab } from "@/components/BookACall";

import Home from "@/pages/Home";
import Work from "@/pages/Work";
import ProjectDetail from "@/pages/ProjectDetail";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import ProductionCity from "@/pages/ProductionCity";
import ServiceLanding from "@/pages/ServiceLanding";
import RssRedirect from "@/pages/RssRedirect";
import BlogArticle from "@/pages/BlogArticle";
import PillarArticle from "@/pages/PillarArticle";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

/**
 * Captures utm_source / utm_medium / utm_campaign from any landing URL and
 * persists to sessionStorage so the contact form can attribute the lead
 * even if the user navigates through Home/Work/About before submitting.
 * Runs once per session; new UTM params overwrite (last-touch attribution).
 */
function UtmCapture() {
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const p = new URLSearchParams(window.location.search);
      const src = p.get("utm_source");
      const med = p.get("utm_medium");
      const cmp = p.get("utm_campaign");
      if (!src && !med && !cmp) return;
      const existing = JSON.parse(sessionStorage.getItem("relatable_utm") || "{}");
      sessionStorage.setItem("relatable_utm", JSON.stringify({
        utm_source: (src || "").slice(0, 60).trim() || existing.utm_source || "",
        utm_medium: (med || "").slice(0, 100).trim() || existing.utm_medium || "",
        utm_campaign: (cmp || "").slice(0, 150).trim() || existing.utm_campaign || "",
        referrer: (document.referrer || "").slice(0, 500) || existing.referrer || "",
      }));
    } catch { /* noop */ }
  }, []);
  return null;
}

function Shell() {
  return (
    <div className="grain">
      <Toaster richColors position="top-right" theme="dark" />
      <Nav />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/work" element={<Work />} />
        <Route path="/work/:slug" element={<ProjectDetail />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/production/bangalore" element={<ProductionCity citySlug="bangalore" />} />
        <Route path="/production/bengaluru" element={<Navigate to="/production/bangalore" replace />} />
        <Route path="/insights" element={<Navigate to="/#insights" replace />} />
        <Route path="/services/:slug" element={<ServiceLanding />} />
        <Route path="/insights/:slug" element={<BlogArticle />} />
        {/* Long-form pillar-authority articles — content-driven topical clusters.
            Data + slugs live in /src/data/pillar-content.js. Any slug not listed
            there redirects to home via PillarArticle. */}
        <Route path="/brand-film-cost-india" element={<PillarArticle slug="brand-film-cost-india" />} />
        <Route path="/relatable-vs-ad-agency" element={<PillarArticle slug="relatable-vs-ad-agency" />} />
        <Route path="/relatable-vs-freelance-crew" element={<PillarArticle slug="relatable-vs-freelance-crew" />} />
        <Route path="/in-house-vs-external-production" element={<PillarArticle slug="in-house-vs-external-production" />} />
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/rss" element={<RssRedirect />} />
        <Route path="/feed" element={<RssRedirect />} />
        <Route path="*" element={<Home />} />
      </Routes>
      <Footer />
      <BookACallFab />
    </div>
  );
}

export default function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <ScrollToTop />
        <UtmCapture />
        <Shell />
      </BrowserRouter>
    </HelmetProvider>
  );
}
